import { Injectable } from '@angular/core';
import { Observable, from, combineLatest, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { Firestore, doc, getDoc, collection, getDocs } from '@angular/fire/firestore';
import { toDate } from '../../../shared/utils/to-date';

export interface IndexValue {
  period: string;
  value: number;
}

export interface IndexBase {
  id: string;
  name: string;
  baseYear: string;
  description?: string;
  isActive: boolean;
  country: string;
  scope: 'GLOBAL' | 'COUNTRY' | 'REGION';
  lastRefresh: Date;
  totalValues: number;
  values: Record<string, number>;
}

export interface IndexRegistry {
  id: string;
  country: string;
  indexType: string;
  baseId: string;
  name: string;
  description: string;
  isActive: boolean;
  scope: 'GLOBAL' | 'COUNTRY' | 'REGION';
  dataPath: string;
  lastRefresh: Date;
  totalValues: number;
  lastUpdatedBy: string;
}

/**
 * Service for managing index data from Firestore
 * Handles reading from the new simplified structure:
 * GLOBAL/INDEXES/BELGIUM/HEALTHINDEX/BASE2013/data
 */
@Injectable({
  providedIn: 'root'
})
export class IndexDataService {

  constructor(private firestore: Firestore) {}

  /**
   * Get all available index registries from GLOBAL/ADMIN/INDEXES_REGISTRY
   */
  getIndexRegistries(): Observable<IndexRegistry[]> {
    // console.log('🔍 IndexDataService: Fetching registries from GLOBAL/ADMIN/INDEXES_REGISTRY');
    const registryCollection = collection(this.firestore, 'GLOBAL/ADMIN/INDEXES_REGISTRY');
    
    return from(getDocs(registryCollection)).pipe(
      map(snapshot => {
        // console.log('📄 Registry snapshot received:', { empty: snapshot.empty, size: snapshot.size, docs: snapshot.docs.length });
        
        const registries = snapshot.docs.map(doc => {
          const data = doc.data();
          // console.log('📋 Registry document:', { id: doc.id, data });
          
          return {
            id: data['id'],
            country: data['country'],
            indexType: data['indexType'],
            baseId: data['baseId'],
            name: data['name'],
            description: data['description'],
            isActive: data['isActive'],
            scope: data['scope'],
            dataPath: data['dataPath'],
            lastRefresh: toDate(data['lastRefresh']) ?? new Date(),
            totalValues: data['totalValues'] || 0,
            lastUpdatedBy: data['lastUpdatedBy'] || 'unknown'
          } as IndexRegistry;
        });
        
        // console.log('✅ Parsed registries:', registries);
        return registries;
      }),
      catchError(error => {
        console.error('❌ Error fetching index registries:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        return of([]);
      })
    );
  }

  /**
   * Get Belgium Health Index BASE 2013 data
   */
  getBelgiumHealthIndexBase2013(): Observable<IndexBase | null> {
    const docRef = doc(this.firestore, 'GLOBAL/INDEXES/BELGIUM/HEALTHINDEX/BASE2013/data');
    
    return from(getDoc(docRef)).pipe(
      map(docSnapshot => {
        if (!docSnapshot.exists()) {
          return null;
        }

        const data = docSnapshot.data();
        return {
          id: data['id'],
          name: data['name'],
          baseYear: data['baseYear'],
          description: data['description'],
          isActive: data['isActive'],
          country: data['country'],
          scope: data['scope'],
          lastRefresh: toDate(data['lastRefresh']) ?? new Date(),
          totalValues: data['totalValues'] || 0,
          values: data['values'] || {}
        } as IndexBase;
      }),
      catchError(error => {
        console.error('Error fetching Belgium Health Index BASE 2013:', error);
        return of(null);
      })
    );
  }

  /**
   * Get index data by registry information
   */
  getIndexDataByRegistry(registry: IndexRegistry): Observable<IndexBase | null> {
    // Extract the document path from registry.dataPath
    // Format: GLOBAL/INDEXES/BELGIUM/HEALTHINDEX/BASE2013/data
    // console.log(`🔍 IndexDataService: Fetching index data from path: ${registry.dataPath}`);
    const docRef = doc(this.firestore, registry.dataPath);
    
    return from(getDoc(docRef)).pipe(
      map(docSnapshot => {
        // console.log(`📄 Index data snapshot for ${registry.dataPath}:`, { exists: docSnapshot.exists(), id: docSnapshot.id, hasData: !!docSnapshot.data() });
        
        if (!docSnapshot.exists()) {
          console.warn(`⚠️ Document does not exist at path: ${registry.dataPath}`);
          return null;
        }

        const data = docSnapshot.data();
        // console.log(`📊 Index data received:`, { id: data?.['id'], name: data?.['name'], country: data?.['country'], isActive: data?.['isActive'], valuesCount: data?.['values'] ? Object.keys(data['values']).length : 0 });
        
        const indexBase = {
          id: data['id'],
          name: data['name'],
          baseYear: data['baseYear'],
          description: data['description'],
          isActive: data['isActive'],
          country: data['country'],
          scope: data['scope'],
          lastRefresh: toDate(data['lastRefresh']) ?? new Date(),
          totalValues: data['totalValues'] || 0,
          values: data['values'] || {}
        } as IndexBase;
        
        // console.log(`✅ Parsed index base:`, indexBase);
        return indexBase;
      }),
      catchError(error => {
        console.error(`❌ Error fetching index data from ${registry.dataPath}:`, error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          path: registry.dataPath
        });
        return of(null);
      })
    );
  }

  /**
   * Get all available index bases by loading from registries
   */
  getAllIndexBases(): Observable<IndexBase[]> {
    return this.getIndexRegistries().pipe(
      switchMap(registries => {
        if (registries.length === 0) {
          return of([]);
        }
        
        // Create observables for each registry
        const indexObservables = registries.map(registry => 
          this.getIndexDataByRegistry(registry)
        );
        
        return combineLatest(indexObservables);
      }),
      // Filter out nulls and convert to IndexBase[]
      map((indexBases: (IndexBase | null)[]) => 
        indexBases.filter((base): base is IndexBase => base !== null)
      )
    );
  }

  /**
   * Convert values object to array format for UI compatibility
   */
  convertValuesToArray(valuesObject: Record<string, number>): IndexValue[] {
    return Object.entries(valuesObject)
      .map(([period, value]) => ({
        period,
        value
      }))
      .sort((a, b) => b.period.localeCompare(a.period)); // Sort newest first
  }

  /**
   * Get formatted values for a specific index base
   */
  getFormattedValues(indexBase: IndexBase): IndexValue[] {
    return this.convertValuesToArray(indexBase.values);
  }
} 